import { DRAFT_STATES } from "@consts";
import { isNotCodefullAgent } from "@utils";

export const SYSTEM_MODE_KEY = "systemMode";
export const PREVIOUS_SYSTEM_MODE_KEY = "previousSystemMode";
export const SYSTEM_MODES = {
  READ_ONLY: "READ_ONLY",
  EDIT: "EDIT",
  PREVIEW: "PREVIEW"
};

// Accepts a single permission or an array; an array is treated as OR (any one grants
// access). The function always reads live state, but a fresh identity is issued on
// every permissions write — the identity change is what makes zustand subscribers of
// `hasPermission` re-render (a never-changing function answered correctly but notified
// no one, so the left panel stayed stale on account switches).
const buildHasPermission = (get) => (permission) => {
  const { userPermissions } = get();
  const required = Array.isArray(permission) ? permission : [permission];
  return required.some((p) => userPermissions?.permissions?.includes(p));
};

const createAppSlice = (set, get) => ({
  httpReady: false,
  systemMode: localStorage.getItem(SYSTEM_MODE_KEY) || SYSTEM_MODES.READ_ONLY,
  previousSystemMode: localStorage.getItem(PREVIOUS_SYSTEM_MODE_KEY) || "",
  openWidgetInitially: localStorage.getItem(SYSTEM_MODE_KEY) === SYSTEM_MODES.PREVIEW,
  isWebSnippetOpen: false,
  isDiscardingChanges: false,
  pageName: "",
  showHeaderSkeleton: true,
  showHeader: true,
  userData: {},
  userPermissions: {},
  previewToken: null,
  selectedAccountId: null,
  selectedAssistant: null,
  isAssistantPreviewable: false,
  accounts: null,
  isLoggedInToLooker: false,
  additionalBreadcrumbs: [],
  couldNotSetUpAssistant: false,
  hasPageErrorType: null,
  setDraft: (value) => set({ draft: value }),
  setPageError: (errorOrigin, error) => {
    const { pageName } = get();

    console.error(
      `${errorOrigin ?? "Error"}: \n`,
      `${error?.response?.statusText || error} \n`,
      `PageName: ${pageName} \n`,
      `URL: ${window.location?.href} \n`
    );

    set({ hasPageErrorType: error?.response?.status || 400 });
  },

  setHttpReady: (httpReady) => set({ httpReady }),
  setSystemMode: (newSystemMode) => {
    const {
      systemMode,
      updateConfigAndTriplets,
      closeSpeechPlayer
    } = get();
    closeSpeechPlayer();
    updateConfigAndTriplets(newSystemMode);
    localStorage.setItem("systemMode", newSystemMode);
    localStorage.setItem("previousSystemMode", systemMode);
    set({
      systemMode: newSystemMode,
      previousSystemMode: systemMode
    });
  },

  updateConfigAndTriplets: (newSystemMode) => {
    const {
      systemMode: storeSystemMode,
      currentTriplets,
      originalTriplets,
      collections,
      collectionConfig,
      selectedCollection,
      originalCollectionConfig,
      collectionSpec,
      validationResults,
      languages,
      draft,
      policiesData
    } = get();
    const isPreviewMode = newSystemMode === SYSTEM_MODES.PREVIEW || storeSystemMode === SYSTEM_MODES.PREVIEW;
    if (isPreviewMode) return;
    const isEditMode = (newSystemMode || storeSystemMode) === SYSTEM_MODES.EDIT;
    const updatedValidationResults = isEditMode && draft ? validationResults.draft : validationResults.live;
    const updatedCurrentSelectedConfig = isEditMode && draft ? collectionConfig : originalCollectionConfig;
    const updatedCurrentTriplets = isEditMode && draft && currentTriplets?.length ? currentTriplets : originalTriplets;
    const updatedCurrentCollections = isEditMode && draft ? { ...collections, currentConfig: collections?.configRef }
      : { ...(collections || {}), currentConfig: collections?.originalConfig };
    const updatedCurrentLanguages = isEditMode && draft ? languages.draft : languages.live;
    const updatedCurrentPolicies = isEditMode && draft ? policiesData?.policies?.draft : policiesData?.policies?.live;
    const updatedCurrentPoliciesSpec = isEditMode && draft ? policiesData?.spec?.draft : policiesData?.spec?.live;
    const updatedCurrentSelectedPolicy = isEditMode && draft ? policiesData?.selectedPolicy?.draft : policiesData?.selectedPolicy?.live;
    let policiesDataObj;
    if (policiesData) {
      policiesDataObj = {
        ...policiesData,
        policies: {
          ...policiesData?.policies,
          current: updatedCurrentPolicies,
          local: updatedCurrentPolicies
        },
        selectedPolicy: {
          ...policiesData?.selectedPolicy,
          current: updatedCurrentSelectedPolicy,
          local: updatedCurrentSelectedPolicy
        },
        spec: {
          ...policiesData?.spec,
          current: updatedCurrentPoliciesSpec
        }
      };
    }
    set({
      selectedCollection: selectedCollection?.key && updatedCurrentSelectedConfig ? {
        ...updatedCurrentSelectedConfig,
        spec: collectionSpec
      } : null,
      collections: updatedCurrentCollections,
      displayedTriplets: updatedCurrentTriplets,
      languages: {
        ...languages,
        current: updatedCurrentLanguages
      },
      validationResults: {
        ...validationResults,
        current: updatedValidationResults
      },
      policiesData: policiesDataObj
    });
  },

  setIsWebSnippetOpen: isWebSnippetOpen => set({ isWebSnippetOpen }),
  setPageName: value => set({ pageName: value }),
  setShowHeaderSkeleton: value => set({ showHeaderSkeleton: value }),
  setShowHeader: value => set({ showHeader: value }),

  isReadOnlyMode: () => {
    const { draft, isDraftOwner } = get();
    return (
      draft?.status === DRAFT_STATES.READ_ONLY
      || (draft?.userEmail && !isDraftOwner())
    );
  },

  isDraftOwner: () => {
    const { userData, draft } = get();
    return draft?.userEmail === userData?.email;
  },

  setIsLoggedInToLooker: (isLoggedIn) => {
    set({ isLoggedInToLooker: isLoggedIn });
  },

  setDashboardSettings: (dashboardSettings) => {
    set({ dashboardSettings });
  },

  setSelectedAccountId: (selectedAccountId) => {
    const { selectedAccountId: previousAccountId, clearAgent } = get();
    if (previousAccountId && previousAccountId !== selectedAccountId) {
      clearAgent();
    }
    set({ selectedAccountId });
  },

  setupAccounts: (accounts) => {
    set({ accounts });
  },

  // Permissions gate the UI only — the server authorizes every request on its own.
  // `accountId` is the account they were resolved for (null = the account-independent
  // bootstrap set), and `fromServer` distinguishes a server-resolved set from the
  // token-claim seed, so the seed can be upgraded in place once the server answers.
  setPermissions: ({ permissions, accountId = null, fromServer = false }) => {
    const { userData } = get();
    const userPermissions = {
      permissions,
      accountId,
      fromServer,
      userId: userData?.id
    };
    set({ userPermissions, hasPermission: buildHasPermission(get) });
  },

  setUserData: (userData) => {
    set({ userData });
  },

  setPreviewToken: (previewToken) => {
    set({ previewToken });
  },

  clearAccountAndAssistant: () => {
    set({
      hasPageErrorType: null,
      selectedAccountId: null,
      selectedAssistant: null,
      assistants: [],
      isLoggedInToLooker: false
    });
  },

  clearAgent: () => {
    set({
      selectedAssistant: null,
      isLoggedInToLooker: false,
      hasPageErrorType: null
    });
  },

  setNonCodelessData: async () => {
    const { initTriplets, getKnowledgeGraph } = get();

    await initTriplets();
    getKnowledgeGraph();
  },

  setSelectedAssistant: (assistant) => {
    set({
      hasPageErrorType: null,
      isLoggedInToLooker: false,
      selectedAssistant: assistant,
      currentSelectedLanguage: null
    });

    const {
      clearTriplets,
      resetConversationsState
    } = get();

    clearTriplets();
    resetConversationsState();
  },

  hasPermission: buildHasPermission(get),
  isCodeless: () => {
    const { selectedAssistant } = get();
    return isNotCodefullAgent(selectedAssistant?.assistantId);
  }
});

export default createAppSlice;
